/**
 * DeviceAdminSection — Innstillinger → Administrasjon → Kjøkkenskjermer (#142).
 *
 * PIN virker bare på en enhet en voksen har rullet inn. Her gjør adminen
 * nettleseren den står ved til kjøkkenskjerm (eller gir den nytt navn), og ser
 * og tilbakekaller familiens enheter.
 *
 * #159 (steg 9c, Stell_Admin): et eget glasskort øverst i Admin, med en rad
 * per enhet (prikk, navn, linje under og Tilbakekall) og navnefeltet og
 * knappen for å rulle inn denne enheten under lista.
 *
 * Alt bak requireAdminScope: listen viser «Lås opp» ved 403, og mutasjonene
 * går gjennom `runGuarded`, som åpner <AdminReauthModal> og prøver igjen etter
 * vellykket opplåsing — samme mønster som MemberAdminSection.
 */
import { useId, useState } from 'react';
import { createPortal } from 'react-dom';
import type { FormEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceRow } from '@stell/shared';
import { Button, ErrorState, LoadingState, Pill, RelativeTime } from '../../components/primitives';
import { AdminReauthModal } from './AdminReauthModal';
import { AdminScopeLocked } from './AdminScopeLocked';
import { isAdminScopeError } from './useMembers';
import { useAdminDevices, useEnrollDevice, useRevokeDevice } from './useDevices';
import { ADMIN_CARD, ADMIN_LABEL } from './adminStyles';

export function DeviceAdminSection(): ReactElement {
  const { t } = useTranslation();
  const list = useAdminDevices();
  const enrollMut = useEnrollDevice();
  const revokeMut = useRevokeDevice();

  const current = list.data?.find((d) => d.current && !d.revoked_at) ?? null;
  // null = brukeren har ikke rørt feltet; vis da enhetens navn eller standarden.
  const [labelDraft, setLabelDraft] = useState<string | null>(null);
  const label = labelDraft ?? current?.label ?? t('devices.label_default');
  const [error, setError] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<DeviceRow | null>(null);
  const revokeTitleId = useId();
  const [reauth, setReauth] = useState<{ open: boolean; retry: (() => void) | null }>({
    open: false,
    retry: null,
  });

  async function runGuarded(fn: () => Promise<void>): Promise<void> {
    try {
      await fn();
    } catch (err) {
      if (isAdminScopeError(err)) {
        setReauth({ open: true, retry: () => void runGuarded(fn) });
        return;
      }
      setError(t('devices.error'));
    }
  }

  function submitEnroll(e: FormEvent): void {
    e.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) {
      setError(t('devices.label_required'));
      return;
    }
    setError(null);
    void runGuarded(async () => {
      await enrollMut.mutateAsync({ label: trimmed });
      setLabelDraft(null);
    });
  }

  function doRevoke(row: DeviceRow): void {
    setError(null);
    setConfirmRevoke(null);
    void runGuarded(() => revokeMut.mutateAsync(row.id));
  }

  function onReauthSuccess(): void {
    const retry = reauth.retry;
    setReauth({ open: false, retry: null });
    retry?.();
  }

  if (list.isError && isAdminScopeError(list.error)) {
    return (
      <section className={`${ADMIN_CARD} flex flex-col gap-3`} data-testid="device-admin-section">
        <SectionHeading />
        <AdminScopeLocked testIdPrefix="device" message={t('devices.scope_locked')} />
      </section>
    );
  }

  return (
    <section className={`${ADMIN_CARD} flex flex-col gap-3`} data-testid="device-admin-section">
      <SectionHeading />
      <p className="m-0 text-caption text-ink-2">{t('devices.section_body')}</p>

      {list.isLoading ? (
        <LoadingState lines={2} />
      ) : list.isError ? (
        <ErrorState variant="inline" heading={t('devices.load_error')} onRetry={() => void list.refetch()} />
      ) : (list.data ?? []).length === 0 ? (
        <p className="m-0 text-sm text-ink-2" data-testid="device-empty">
          {t('devices.empty')}
        </p>
      ) : (
        <ul className="m-0 list-none p-0" data-testid="device-list">
          {(list.data ?? []).map((d) => {
            const isCurrent = d.current && !d.revoked_at;
            return (
              <li
                key={d.id}
                data-testid={`device-row-${d.id}`}
                className={`flex min-h-13 items-center gap-3 border-b border-card-line py-2 last:border-b-0 ${d.revoked_at ? 'opacity-50' : ''}`}
              >
                {/* Prikken er bare pynt: «Denne enheten» og «Tilbakekalt» står i tekst. */}
                <span
                  aria-hidden="true"
                  className={`size-2.5 shrink-0 rounded-full ${isCurrent ? 'bg-good' : 'bg-ink-2'}`}
                />
                <div className="min-w-0 flex-1">
                  <p className="m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-extrabold text-ink">
                    <span className="min-w-0 truncate">{d.label}</span>
                    {isCurrent ? <Pill variant="ok">{t('devices.current_badge')}</Pill> : null}
                  </p>
                  <p className="m-0 text-caption text-ink-2">
                    {d.enrolled_by_name ? `${t('devices.enrolled_by', { name: d.enrolled_by_name })} · ` : ''}
                    {d.revoked_at ? (
                      t('devices.revoked')
                    ) : (
                      <>
                        {t('devices.last_seen')} <RelativeTime instant={d.last_seen_at} />
                      </>
                    )}
                  </p>
                </div>
                {d.revoked_at ? null : (
                  <Button
                    variant="danger"
                    size={44}
                    onClick={() => setConfirmRevoke(d)}
                    data-testid={`device-revoke-${d.id}`}
                  >
                    {t('devices.revoke')}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Navnet og knappen under lista, som «Rull inn denne enheten» i skissen. */}
      {current ? (
        <p className="m-0 text-caption text-ink-2" data-testid="device-current-note">
          {t('devices.this_device', { label: current.label })}
        </p>
      ) : null}

      <form className="flex flex-wrap items-end gap-2" onSubmit={submitEnroll}>
        <label className="flex min-w-40 flex-1 flex-col gap-1 text-caption font-bold text-ink-2">
          {t('devices.label_label')}
          <input
            data-testid="device-label-input"
            className="field"
            maxLength={60}
            value={label}
            onChange={(e) => setLabelDraft(e.target.value)}
          />
        </label>
        <Button
          type="submit"
          variant="secondary"
          size={44}
          disabled={enrollMut.isPending}
          data-testid="device-enroll"
        >
          {current ? t('devices.rename') : t('devices.enroll')}
        </Button>
      </form>

      {error ? (
        <p className="m-0 text-sm text-bad" role="alert" data-testid="device-error">
          {error}
        </p>
      ) : null}

      {/* Portal: glasskortet (backdrop-filter) ville ellers vært ramma for
          `fixed`, så dimmingen dekket bare kortet (#159). */}
      {confirmRevoke
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby={revokeTitleId}
              className="fixed inset-0 z-50 flex items-center justify-center bg-dim p-4"
              data-testid="device-revoke-confirm"
            >
              <div className="w-full max-w-sm space-y-4 rounded-lg bg-sheet p-6 shadow-lg">
                <h2 id={revokeTitleId} className="text-heading-s font-extrabold text-ink">
                  {t('devices.revoke_confirm_title', {
                    label: confirmRevoke.label,
                  })}
                </h2>
                <p className="text-sm text-ink-2">{t('devices.revoke_confirm_body')}</p>
                <div className="flex justify-end gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setConfirmRevoke(null)}
                    data-testid="device-revoke-cancel"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => doRevoke(confirmRevoke)}
                    data-testid="device-revoke-confirm-btn"
                  >
                    {t('devices.revoke')}
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      <AdminReauthModal
        open={reauth.open}
        onClose={() => setReauth({ open: false, retry: null })}
        onSuccess={onReauthSuccess}
      />
    </section>
  );
}

function SectionHeading(): ReactElement {
  const { t } = useTranslation();
  return <h2 className={ADMIN_LABEL}>{t('devices.section_label')}</h2>;
}
